import { AgGridReact } from "ag-grid-react";
import { useCallback, useRef } from "react";

import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { useQzPrint } from "src/hooks/useQzPrint";
import {
	buildCommonCodeRequirements,
	KSK2140_R01_INSPECT_ROW_VALIDATE,
} from "src/utils/commonCode/commonCodeRequirementConfig";
import { useCommonCodeValidate } from "src/utils/commonCode/CommonCodeValidateContext";
import { validateString } from "src/utils/string/stringUtil";
import {
	ShowSuccessFetch,
	showError,
	showWarning,
} from "src/utils/toast/toastUtil";

import {
	// deleteKSK2140_R01,
	createKSK2140_R01LotNo,
	getKSK2140_R01List,
	getKSK2140_R01WorkStandardOne,
	postKSK2140_R01,
} from "./Api_KSK2140_R01";
import {
	defaultKSK2140_R01InfoDto,
	defaultKSK2140_R01InspectDto,
	defaultKSK2140_R01WorkStandardDto,
	KSK2140_R01DetailDto,
	KSK2140_R01Dto,
	KSK2140_R01HeaderDto,
	KSK2140_R01InfoDto,
	KSK2140_R01InspectDto,
	KSK2140_R01LotDto,
} from "./Dto_KSK2140_R01";
import { useKSK2140_R01Store } from "./Store_KSK2140_R01";
import { KSK2140_R01_buildLabelDataList } from "./labelDataBuilder_KSK2140_R01";
import { flattenKSK2140_R01InspectMap, isAllDetailCheckFinish } from "./Util_KSK2140_R01";

interface ContentKSK2140R01Props {
	gridRef: React.RefObject<AgGridReact<KSK2140_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<KSK2140_R01DetailDto> | null>;
	setMessage: (message: string) => void;
}

export const Content_KSK2140_R01 = ({
	gridRef,
	detailGridRef,
	setMessage,
}: ContentKSK2140R01Props) => {
	const { printBulkLabel } = useQzPrint();
	const {
		KSK2140_R01Filter,
		setKSK2140_R01List,
		selectedKSK2140_R01List, setSelectedKSK2140_R01List,
		setWorkStandardModalVisible, setWorkStandardSheetNo, setKSK2140_R01WorkStandard,
		editKSK2140_R01, setEditKSK2140_R01,
		KSK2140_R01DetailList, setKSK2140_R01DetailList,
		selectedKSK2140_R01DetailList, initSelectedKSK2140_R01DetailList,
		removeKSK2140_R01DetailList,
		deletedKSK2140_R01DetailList, addDeletedKSK2140_R01DetailList, removeDeletedKSK2140_R01DetailList,
		originalKSK2140_R01DetailList,
		KSK2140_R01InspectMap, setKSK2140_R01InspectMap, deletedKSK2140_R01InspectList,
		KSK2140_R01LotList, setKSK2140_R01LotList, deletedKSK2140_R01LotList,
		setScrollTop, setDetailScrollTop,
		initData,
	} = useKSK2140_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { faultyValueList } = useCommonCodeStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const detailRequestIdRef = useRef(0);

	const handleWorkStandardClick = useCallback(async (sheetNo: 1 | 2) => {
		const itemCd = useKSK2140_R01Store.getState().selectedKSK2140_R01List[0]?.itemCd || "";
		if (!validateString(itemCd)) {
			showWarning("생산실적을 먼저 선택하세요.");
			return;
		}

		const sheetKey = sheetNo === 1 ? "standardSheet" : "standardSheet2";
		const cached = useKSK2140_R01Store.getState().KSK2140_R01WorkStandard;
		let sheet = cached?.itemCd === itemCd ? cached?.[sheetKey] : undefined;

		if (!validateString(sheet || "")) {
			setLoadingSpinner(true);
			try {
				const response = await getKSK2140_R01WorkStandardOne(itemCd, sheetNo);
				if (response.data?.successFlag === false) return;
				if ((useKSK2140_R01Store.getState().selectedKSK2140_R01List[0]?.itemCd || "") !== itemCd) return;

				sheet = response.data.data?.[sheetKey];
				const currentCache = useKSK2140_R01Store.getState().KSK2140_R01WorkStandard;
				setKSK2140_R01WorkStandard({
					...(currentCache?.itemCd === itemCd ? currentCache : defaultKSK2140_R01WorkStandardDto),
					itemCd,
					workplaceCd: response.data.data?.workplaceCd || "Q100",
					workplaceNm: response.data.data?.workplaceNm || (currentCache?.itemCd === itemCd ? currentCache.workplaceNm : ""),
					[sheetKey]: sheet ?? "",
				});
			} catch (error) {
				console.error("작업표준 조회 실패:", error);
				showWarning("작업표준 조회에 실패했습니다.");
				return;
			} finally {
				setLoadingSpinner(false);
			}
		}

		if (!validateString(sheet || "")) {
			showWarning(`등록된 작업표준${sheetNo}이 없습니다.`);
			return;
		}
		setWorkStandardSheetNo(sheetNo);
		setWorkStandardModalVisible(true);
	}, [setKSK2140_R01WorkStandard, setLoadingSpinner, setWorkStandardModalVisible, setWorkStandardSheetNo]);

	const defaultKSK2140_R01InspectRow = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(KSK2140_R01_INSPECT_ROW_VALIDATE, { faultyValueList }),
		);

		if (!commonResult.valid) {
			return {
				...defaultKSK2140_R01InspectDto,
				inserted: true,
			};
		}

		return {
			...defaultKSK2140_R01InspectDto,
			badCase: faultyValueList[0].minorCd,
			inserted: true,
		};
	}, [faultyValueList, validateCommonCodeLists]);

	const hasPendingChanges =
		Array.from(KSK2140_R01InspectMap.values()).some((list) =>
			list.some((item) => item.inserted),
		) ||
		deletedKSK2140_R01InspectList.length > 0 ||
		KSK2140_R01LotList.some((item) => item.inserted) ||
		deletedKSK2140_R01LotList.length > 0;

	const fetchData = useCallback(async (options?: { skipPendingCheck?: boolean }) => {
		if (!options?.skipPendingCheck && hasPendingChanges) {
			showWarning("변경 내용을 저장하거나 초기화한 후 다시 조회해 주세요.");
			return;
		}

		gridRef.current?.api.stopEditing();
		detailGridRef.current?.api.stopEditing();
		detailRequestIdRef.current++;
		setLoadingSpinner(true);
		try {
			setScrollTop(null);
			setDetailScrollTop(null);
			const response = await getKSK2140_R01List(
				"search",
				KSK2140_R01Filter,
			);
			const productOrderList = response.data.data ?? [];
			setKSK2140_R01List(productOrderList);
			setKSK2140_R01DetailList([]);
			setKSK2140_R01InspectMap(new Map());
			setKSK2140_R01LotList([]);
			setSelectedKSK2140_R01List([]);
			setEditKSK2140_R01({
				...defaultKSK2140_R01InfoDto,
				labelPrintAuto: editKSK2140_R01.labelPrintAuto,
			});

			if (productOrderList.length > 0) {
				gridRef.current?.api.ensureIndexVisible(0, "top");
				ShowSuccessFetch(
					setMessage,
					`조회가 완료되었습니다. (총 ${productOrderList.length}건)`,
				);
				setTimeout(() => setMessage(""), 5000);
			} else {
				setMessage("");
				showWarning("조회된 데이터가 없습니다.");
			}
		} catch {
			showError("작업지시 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		KSK2140_R01Filter,
		detailGridRef,
		gridRef,
		hasPendingChanges,
		setKSK2140_R01DetailList,
		setKSK2140_R01InspectMap,
		setKSK2140_R01List,
		setKSK2140_R01LotList,
		setLoadingSpinner,
		setMessage,
		setDetailScrollTop,
		setScrollTop,
		setSelectedKSK2140_R01List,
		setEditKSK2140_R01,
		editKSK2140_R01.labelPrintAuto,
	]);

	const handleDeleteClick = useCallback(() => {
		if (deletedKSK2140_R01DetailList.length > 0) {
			removeDeletedKSK2140_R01DetailList(
				deletedKSK2140_R01DetailList,
			);
			initSelectedKSK2140_R01DetailList();
			return;
		}

		if (!selectedKSK2140_R01DetailList) {
			showWarning("삭제할 투입 LOT을 선택해 주세요.");
			return;
		}

		const selectedList = [selectedKSK2140_R01DetailList];
		const insertedValueIndexList = selectedList
			.filter((item) => item.inserted)
			.map((item) => item.valueIndex.toString());
		const existingValueIndexList = selectedList
			.filter((item) => !item.inserted)
			.map((item) => item.valueIndex.toString());

		if (existingValueIndexList.length > 0) {
			addDeletedKSK2140_R01DetailList(existingValueIndexList);
		}
		if (insertedValueIndexList.length > 0) {
			removeKSK2140_R01DetailList(insertedValueIndexList);
		}
		initSelectedKSK2140_R01DetailList();
	}, [
		addDeletedKSK2140_R01DetailList,
		deletedKSK2140_R01DetailList,
		initSelectedKSK2140_R01DetailList,
		removeDeletedKSK2140_R01DetailList,
		removeKSK2140_R01DetailList,
		selectedKSK2140_R01DetailList,
	]);

	const validateInsertData = (
		info: KSK2140_R01InfoDto,
		selectedList: KSK2140_R01HeaderDto[],
		inspectMap: Map<string, KSK2140_R01InspectDto[]>,
	) => {
		if (!validateString(info.inspectorEmp)) {
			showWarning("검사자를 선택해 주세요.");
			return false;
		}
		if (!validateString(info.approvorEmpno)) {
			showWarning("승인자를 선택해 주세요.");
			return false;
		}
		if (selectedList.length === 0) {
			showWarning("생산실적을 먼저 선택해 주세요.");
			return false;
		}
		const firstItemCd = selectedList[0].itemCd || "";
		if (!selectedList.every((item) => (item.itemCd || "") === firstItemCd)) {
			showWarning("선택한 생산실적의 품목코드가 일치하지 않습니다.");
			return false;
		}
		if (!isAllDetailCheckFinish(KSK2140_R01DetailList)) {
			showWarning("모든 상세 행의 검사를 완료해 주세요.");
			return false;
		}

		for (const [, inspectList] of Array.from(inspectMap)) {
			if (inspectList.length === 0) continue;
			const deletedInspectListToNumber = deletedKSK2140_R01InspectList.map(
				(value) => Number(value),
			);
			const activeInspectList = inspectList.filter(
				(inspect) => !deletedInspectListToNumber.includes(inspect.valueIndex),
			);
			const badCases = activeInspectList
				.map((detail) => detail.badCase)
				.filter((badCase) => badCase);
			const duplicates = badCases.filter(
				(badCase, index) => badCases.indexOf(badCase) !== index,
			);
			if (duplicates.length > 0) {
				const badCaseNames = duplicates.map(
					(badCase) =>
						faultyValueList.find((value) => value.minorCd === badCase)?.codeNm,
				);
				showWarning("중복된 불량사유가 있습니다.\n불량사유: " + badCaseNames.join(", "));
				return false;
			}
		}
		return true;
	};

	const assignLotNoToLists = useCallback(async (currentLotList: KSK2140_R01LotDto[]) => {
		const deletedLotValueIndexList = deletedKSK2140_R01LotList.map((value) => Number(value));
		const activeList = currentLotList.filter(
			(lot) => !deletedLotValueIndexList.includes(lot.valueIndex),
		);

		const newLotList = await Promise.all(
			activeList.map(async (lot) => {
				let generatedLotNo = lot.lotNo;
				const res = await createKSK2140_R01LotNo();
				if (res.data?.successFlag) {
					generatedLotNo = res.data.data;
				}
				return {
					...lot,
					lotNo: generatedLotNo,
				};
			}),
		);

		if (newLotList.length > 0) {
			setKSK2140_R01LotList(newLotList);
		}
		return newLotList;
	}, [deletedKSK2140_R01LotList, setKSK2140_R01LotList]);

	const handleSaveClick = useCallback(async () => {
		const firstSelected = selectedKSK2140_R01List[0];
		const productOrderNo =
			firstSelected?.productOrderNo || firstSelected?.productResultNo;
		if (!productOrderNo) {
			showWarning("작업지시를 먼저 선택해 주세요.");
			return;
		}
		if (!hasPendingChanges) {
			showWarning("저장할 변경사항이 없습니다.");
			return;
		}
		if (!validateInsertData(editKSK2140_R01, selectedKSK2140_R01List, KSK2140_R01InspectMap)) {
			return;
		}

		setLoadingSpinner(true);
		try {
			const newLotList = await assignLotNoToLists(KSK2140_R01LotList);
			if (KSK2140_R01LotList.length > 0 && newLotList.some((lot) => !validateString(lot.lotNo))) {
				showWarning("LOT 번호 발행에 실패하였습니다.");
				return;
			}

			const detailProductResultNo =
				KSK2140_R01DetailList.map((item) => item.basOrderNo)
					.find((basOrderNo) => validateString(basOrderNo)) || "";
			const insertedList: KSK2140_R01Dto = {
				productResultHeaderDto: {
					...firstSelected,
					...editKSK2140_R01,
					productResultNo:
						detailProductResultNo
						|| firstSelected?.productResultNo
						|| "",
				},
				productResultDetailList: KSK2140_R01DetailList,
				prdInspectResultDtoList: flattenKSK2140_R01InspectMap(
					KSK2140_R01InspectMap,
					KSK2140_R01DetailList,
				),
				wmsStockLotDtoList: newLotList,
			};
			const labelDataList = KSK2140_R01_buildLabelDataList(
				newLotList,
				insertedList.productResultHeaderDto,
			);
			// const deletedList = originalKSK2140_R01DetailList
			// 	.filter((item) =>
			// 		deletedKSK2140_R01DetailList.some(
			// 			(valueIndex) => Number(valueIndex) === item.valueIndex,
			// 		),
			// 	)
			// 	.map((item) => ({ ...item, productOrderNo }));

			// if (deletedList.length > 0) {
			// 	await deleteKSK2140_R01(deletedList);
			// }
			await postKSK2140_R01(insertedList);

			detailRequestIdRef.current++;
			setKSK2140_R01DetailList([]);
			setKSK2140_R01LotList([]);
			setKSK2140_R01InspectMap(new Map());
			setSelectedKSK2140_R01List([]);
			setEditKSK2140_R01({
				...defaultKSK2140_R01InfoDto,
				labelPrintAuto: editKSK2140_R01.labelPrintAuto,
			});
			await fetchData({ skipPendingCheck: true });
			ShowSuccessFetch(setMessage, "검사 저장이 완료되었습니다.");
			setTimeout(() => setMessage(""), 5000);

			if (editKSK2140_R01.labelPrintAuto === "Y" && labelDataList.length > 0) {
				try {
					const ok = await printBulkLabel(labelDataList,"label1",);
					if (!ok) {
						showWarning("검사 저장은 완료되었으나 라벨 출력에 실패했습니다. 프린터 설정을 확인해 주세요.");
					}
				} catch (error: unknown) {
					const message = error instanceof Error ? error.message : "알 수 없는 오류";
					showWarning(`검사 저장은 완료되었으나 라벨 출력에 실패했습니다: ${message}`);
				}
			}
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		KSK2140_R01DetailList,
		KSK2140_R01InspectMap,
		KSK2140_R01LotList,
		deletedKSK2140_R01InspectList,
		assignLotNoToLists,
		editKSK2140_R01,
		fetchData,
		hasPendingChanges,
		printBulkLabel,
		selectedKSK2140_R01List,
		setEditKSK2140_R01,
		setKSK2140_R01DetailList,
		setKSK2140_R01InspectMap,
		setKSK2140_R01LotList,
		setLoadingSpinner,
		setMessage,
		setSelectedKSK2140_R01List,
	]);

	const handleInitClick = useCallback(() => {
		gridRef.current?.api.stopEditing();
		gridRef.current?.api.deselectAll();
		detailGridRef.current?.api.stopEditing();
		detailGridRef.current?.api.deselectAll();
		detailRequestIdRef.current++;
		initData();
		setMessage("");
	}, [detailGridRef, gridRef, initData, setMessage]);

	return {
		handleWorkStandardClick,
		fetchData,
		defaultKSK2140_R01InspectRow,
		handleDeleteClick,
		handleSaveClick,
		initData: handleInitClick,
	};
};
